import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const pretty = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Generic repeatable-subrecord editor for contact methods, organizations and
 * affiliations. One component, three configurations — the CRM's repeatable data all shares
 * the same add / list / delete shape.
 */
export default function ContactSubrecords({
  contactId, title, testid, path, rows = [], fields, describe, onChanged, enums,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const add = async () => {
    setBusy(true);
    try {
      const payload = fields.reduce((acc, f) => {
        const value = draft[f.key];
        if (value === undefined || value === "") return acc;
        if (f.group) acc[f.group] = { ...(acc[f.group] || {}), [f.key]: value };
        else acc[f.key] = value;
        return acc;
      }, { ...(fields.find((f) => f.fixed) ? {} : {}) });
      fields.filter((f) => f.fixed).forEach((f) => { payload[f.key] = f.fixed; });
      await api.post(`/crm/contacts/${contactId}/${path}`, payload);
      setDraft({}); setOpen(false); setError("");
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail
        ? "That value isn't valid — check the required fields." : "Couldn't save that.");
    } finally { setBusy(false); }
  };

  const remove = async (rowId) => {
    try {
      await api.delete(`/crm/contacts/${contactId}/${path}/${rowId}`);
      onChanged?.();
    } catch { toast.error("Couldn't remove that"); }
  };

  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-[640]">{title}</h2>
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`${testid}-add`}
          className="inline-flex items-center gap-1 text-sm font-[560] text-primary hover:underline">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      <ul className="mt-3 space-y-1.5">
        {rows.length === 0 && (
          <li className="text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
            Nothing here yet.
          </li>
        )}
        {rows.map((row) => (
          <li key={row.id} data-testid={`${testid}-row-${row.id}`}
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2 text-sm">
            <span className="min-w-0 flex-1 truncate">{describe(row)}</span>
            {row.label && (
              <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[11px] text-foreground/70">
                {pretty(row.label_custom || row.label)}
              </span>
            )}
            <button type="button" onClick={() => remove(row.id)} aria-label="Remove"
              data-testid={`${testid}-remove-${row.id}`}
              className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>

      {open && (
        <div className="mt-3 space-y-3 rounded-lg border border-border bg-secondary/40 p-3"
          data-testid={`${testid}-form`}>
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.filter((f) => !f.fixed).map((f) => (
              <div key={f.key}>
                <Label htmlFor={`${testid}-${f.key}`}>{f.label}</Label>
                {f.options ? (
                  <select id={`${testid}-${f.key}`} value={draft[f.key] || ""} onChange={set(f.key)}
                    data-testid={`${testid}-field-${f.key}`}
                    className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">Select…</option>
                    {(f.options === "enum" ? (enums?.[f.enumKey] || []) : f.options).map((o) => (
                      <option key={o} value={o}>{pretty(o)}</option>
                    ))}
                  </select>
                ) : (
                  <Input id={`${testid}-${f.key}`} value={draft[f.key] || ""} onChange={set(f.key)}
                    maxLength={f.max || 150} data-testid={`${testid}-field-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setOpen(false); setDraft({}); setError(""); }}
              data-testid={`${testid}-cancel`}>Cancel</Button>
            <Button size="sm" onClick={add} disabled={busy} data-testid={`${testid}-save`}>Save</Button>
          </div>
          {error && (
            <p className="text-xs text-destructive" role="alert" data-testid={`${testid}-error`}>
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
